import { useEffect, useMemo, useState } from 'react';

/**
 * The ordered rate catalog served by GET /api/rates/catalog: one list of
 * rate keys, labels and duty levels that Settings → Rates, a provider's
 * Rates tab and the rate agreement page all render from, so every surface
 * shows the same lines in the same order. Fetched once per page load.
 */
let cached = null;
let inflight = null;

const DUTY_ORDER = ['light', 'medium', 'heavy', 'all', 'additional', 'road'];

export const loadRateCatalog = () => {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetch(`${process.env.REACT_APP_API_URL}/api/rates/catalog`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`Could not load the rate catalog (${res.status})`))))
      .then((data) => {
        cached = (data.catalog || []).map((entry) => ({ ...entry, id: entry.key }));
        return cached;
      })
      .finally(() => { inflight = null; });
  }
  return inflight;
};

export default function useRateCatalog() {
  const [catalog, setCatalog] = useState(cached || []);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    loadRateCatalog()
      .then((entries) => { if (active) setCatalog(entries); })
      .catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, []);

  return useMemo(() => {
    const byKey = Object.fromEntries(catalog.map((entry) => [entry.key, entry]));
    const indexOf = (key) => {
      const index = catalog.findIndex((entry) => entry.key === key);
      return index === -1 ? catalog.length : index;
    };
    const dutyIndex = (duty) => {
      const index = DUTY_ORDER.indexOf(duty);
      return index === -1 ? DUTY_ORDER.length : index;
    };
    return {
      catalog,
      loaded: catalog.length > 0,
      error,
      rates: catalog.filter((entry) => entry.category === 'rates'),
      additionalCharges: catalog.filter((entry) => entry.category === 'additionalCharges'),
      byKey,
      labelOf: (key) => byKey[key]?.label || key,
      isMileage: (key) => byKey[key]?.isMileage === true,
      /** Catalog order, then duty level. */
      sortItems: (items = []) =>
        [...items].sort((a, b) => indexOf(a.serviceType) - indexOf(b.serviceType) || dutyIndex(a.dutyLevel) - dutyIndex(b.dutyLevel)),
      /** One line per serviceType + dutyLevel; the last copy wins. */
      dedupeItems: (items = []) => {
        const seen = new Map();
        items.forEach((item) => seen.set(`${item.serviceType}|${item.dutyLevel}`, item));
        return [...seen.values()];
      }
    };
  }, [catalog, error]);
}
